import React, { useState } from 'react';
import { CreateWhatsAppNotificationContact } from '../../types/whatsapp';

interface ContactFormProps {
  contactos: CreateWhatsAppNotificationContact[];
  onAddContact: (contact: CreateWhatsAppNotificationContact) => void;
  onRemoveContact: (index: number) => void;
  onUpdateContact: (index: number, contact: CreateWhatsAppNotificationContact) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  contactos,
  onAddContact,
  onRemoveContact,
  onUpdateContact
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContact, setNewContact] = useState<CreateWhatsAppNotificationContact>({
    numeroTelefono: '',
    nombre: '',
    apellido: '',
    email: '',
    esActivo: true,
    recibeAlertas: true,
    recibeReportes: true,
    recibeSolicitudesAseo: true
  });

  const handleAddContact = () => {
    if (newContact.numeroTelefono && newContact.nombre) {
      onAddContact(newContact);
      setNewContact({
        numeroTelefono: '',
        nombre: '',
        apellido: '',
        email: '',
        esActivo: true,
        recibeAlertas: true,
        recibeReportes: true,
        recibeSolicitudesAseo: true
      });
      setShowAddForm(false);
    }
  };

  const handleUpdateContact = (index: number, field: keyof CreateWhatsAppNotificationContact, value: any) => {
    const updatedContact = { ...contactos[index], [field]: value };
    onUpdateContact(index, updatedContact);
  };

  const formatPhoneNumber = (phone: string) => {
    // Formato básico para mostrar números de teléfono
    if (phone.startsWith('+56')) {
      return `+56 ${phone.slice(3, 4)} ${phone.slice(4, 8)} ${phone.slice(8)}`;
    }
    return phone;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-900">Contactos de notificación</h4>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Agregar contacto
        </button>
      </div>

      {/* Lista de contactos existentes */}
      {contactos.length > 0 && (
        <div className="mb-6 border border-gray-200 rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teléfono
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notificaciones
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contactos.map((contacto, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contacto.nombre} {contacto.apellido}
                        </div>
                        {contacto.email && (
                          <div className="text-sm text-gray-500">{contacto.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {formatPhoneNumber(contacto.numeroTelefono)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <div className="flex space-x-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={contacto.recibeAlertas}
                            onChange={(e) => handleUpdateContact(index, 'recibeAlertas', e.target.checked)}
                            className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-1 text-xs text-gray-700">Alertas</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={contacto.recibeReportes}
                            onChange={(e) => handleUpdateContact(index, 'recibeReportes', e.target.checked)}
                            className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-1 text-xs text-gray-700">Reportes</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={contacto.recibeSolicitudesAseo}
                            onChange={(e) => handleUpdateContact(index, 'recibeSolicitudesAseo', e.target.checked)}
                            className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                          <span className="ml-1 text-xs text-gray-700">Solicitudes</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={contacto.esActivo}
                          onChange={(e) => handleUpdateContact(index, 'esActivo', e.target.checked)}
                          className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-1 text-xs text-gray-700">Activo</span>
                      </label>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        onClick={() => onRemoveContact(index)}
                        className="text-red-600 hover:text-red-900 text-xs"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Formulario para agregar nuevo contacto */}
      {showAddForm && (
        <div className="border border-gray-200 rounded-md p-4 mb-4 bg-gray-50">
          <h5 className="text-sm font-medium text-gray-900 mb-3">Nuevo contacto</h5>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Número de teléfono *
              </label>
              <input
                type="tel"
                value={newContact.numeroTelefono}
                onChange={(e) => setNewContact(prev => ({ ...prev, numeroTelefono: e.target.value }))}
                placeholder="+56971234567"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Nombre *
              </label>
              <input
                type="text"
                value={newContact.nombre}
                onChange={(e) => setNewContact(prev => ({ ...prev, nombre: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Apellido
              </label>
              <input
                type="text"
                value={newContact.apellido}
                onChange={(e) => setNewContact(prev => ({ ...prev, apellido: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
          </div>
          
          <div className="mt-4 flex flex-wrap gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newContact.esActivo}
                onChange={(e) => setNewContact(prev => ({ ...prev, esActivo: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Contacto activo</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newContact.recibeAlertas}
                onChange={(e) => setNewContact(prev => ({ ...prev, recibeAlertas: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Recibe alertas</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newContact.recibeReportes}
                onChange={(e) => setNewContact(prev => ({ ...prev, recibeReportes: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Recibe reportes</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newContact.recibeSolicitudesAseo}
                onChange={(e) => setNewContact(prev => ({ ...prev, recibeSolicitudesAseo: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Recibe solicitudes de aseo</span>
            </label>
          </div>

          <div className="mt-4 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleAddContact}
              disabled={!newContact.numeroTelefono || !newContact.nombre}
              className="px-3 py-1 border border-transparent rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Agregar
            </button>
          </div>
        </div>
      )}

      {contactos.length === 0 && !showAddForm && (
        <div className="text-center py-6 text-sm text-gray-500">
          No hay contactos configurados. Agrega al menos un contacto para recibir notificaciones.
        </div>
      )}
    </div>
  );
};

export default ContactForm;
